import { supabase } from '@/lib/supabase';
import { format } from 'date-fns';
import { 
  extractKeywords, 
  detectLanguage, 
  clusterFeedback, 
  analyzeResponseFeedbackCorrelation 
} from '@/utils/simple-multilingual';

export interface FeedbackAnalysis {
  feedbackTrend: {
    date: string;
    positive: number;
    negative: number;
    total: number;
    positiveRate: number;
  }[];
  topPositiveTopics: { topic: string; count: number }[];
  topNegativeTopics: { topic: string; count: number }[];
  sessionTopics: { topic: string; positiveCount: number; negativeCount: number }[];
  knowledgeImprovementSuggestions?: {
    document_id: string;
    title: string;
    negative_feedback_count: number;
    total_usage: number;
    negative_ratio: number;
    topic: string;
    suggestion: string;
    confidence: number;
  }[];
  ruleImprovementSuggestions?: {
    rule_id: number;
    rule: string;
    pattern: string; 
    negative_feedback_count: number;
    total_usage: number;
    negative_ratio: number;
    suggestion: string;
    impact: number;
  }[];
}

// Generate a feedback report for scheduled reporting
export async function generateFeedbackReport(
  startDate: Date,
  endDate: Date,
  topic?: string,
  formatType: 'pdf' | 'excel' = 'pdf'
): Promise<{ url: string; filename: string }> {
  const analysis = await analyzeFeedback(startDate, endDate, topic);
  
  // This is a placeholder function that would be implemented in the edge function
  // It would generate a PDF or Excel file with the analysis data
  const reportData = {
    url: `https://example.com/reports/feedback-${Date.now()}.${formatType}`,
    filename: `FeedbackReport-${formatType === 'pdf' ? 'PDF' : 'Excel'}-${format(startDate, 'yyyy-MM-dd')}-to-${format(endDate, 'yyyy-MM-dd')}.${formatType}`
  };
  
  return reportData;
}

export async function analyzeFeedback(
  startDate: Date,
  endDate: Date,
  topic?: string
): Promise<FeedbackAnalysis> {
  try {
    // Build the base query for feedback data
    let feedbackQuery = supabase
      .from('assistant_feedback')
      .select(`
        id, 
        feedback_type, 
        created_at, 
        session_id, 
        message_id
      `)
      .gte('created_at', startDate.toISOString())
      .lte('created_at', endDate.toISOString());
    
    // Get all feedback entries within date range
    const { data: feedbackEntries, error: feedbackError } = await feedbackQuery;
    
    if (feedbackError) {
      console.error('Error fetching feedback:', feedbackError);
      throw new Error('Failed to fetch feedback data');
    }
    
    // Get session data for all the sessions with feedback
    const sessionIds = [...new Set(feedbackEntries.map(entry => entry.session_id))];
    const { data: sessionData, error: sessionError } = await supabase
      .from('assistant_sessions')
      .select('id, topic')
      .in('id', sessionIds);
    
    if (sessionError) {
      console.error('Error fetching sessions:', sessionError);
      throw new Error('Failed to fetch session data');
    }
    
    // Create a mapping of session ID to topic
    const sessionTopicMap: Record<string, string> = {};
    sessionData?.forEach(session => {
      sessionTopicMap[session.id] = session.topic;
    });
    
    // Filter feedback by topic if specified
    let filteredFeedback = feedbackEntries;
    if (topic) {
      filteredFeedback = feedbackEntries.filter(entry => 
        sessionTopicMap[entry.session_id] === topic
      );
    }
    
    // Generate feedback trend by date
    const feedbackByDate: Record<string, { positive: number; negative: number; total: number }> = {};
    
    filteredFeedback.forEach(entry => {
      const date = new Date(entry.created_at).toISOString().split('T')[0];
      
      if (!feedbackByDate[date]) {
        feedbackByDate[date] = { positive: 0, negative: 0, total: 0 };
      }
      
      if (entry.feedback_type === 'positive') {
        feedbackByDate[date].positive += 1;
      } else {
        feedbackByDate[date].negative += 1;
      }
      
      feedbackByDate[date].total += 1;
    });
    
    const feedbackTrend = Object.entries(feedbackByDate).map(([date, counts]) => ({
      date,
      positive: counts.positive,
      negative: counts.negative,
      total: counts.total,
      positiveRate: counts.total > 0 ? (counts.positive / counts.total) * 100 : 0
    })).sort((a, b) => a.date.localeCompare(b.date));
    
    // Group feedback by session topic
    const topicCounts: Record<string, { positive: number; negative: number }> = {};
    
    filteredFeedback.forEach(entry => {
      const topic = sessionTopicMap[entry.session_id] || 'unknown';
      
      if (!topicCounts[topic]) {
        topicCounts[topic] = { positive: 0, negative: 0 };
      }
      
      if (entry.feedback_type === 'positive') {
        topicCounts[topic].positive += 1;
      } else {
        topicCounts[topic].negative += 1;
      }
    });
    
    const sessionTopics = Object.entries(topicCounts).map(([topic, counts]) => ({
      topic,
      positiveCount: counts.positive,
      negativeCount: counts.negative
    }));
    
    // Get message content for feedback
    const messageIds = filteredFeedback.map(entry => entry.message_id);
    const { data: messageData, error: messageError } = await supabase
      .from('assistant_messages')
      .select('id, content')
      .in('id', messageIds);
    
    if (messageError) {
      console.error('Error fetching messages:', messageError);
      throw new Error('Failed to fetch message data');
    }
    
    // Create message content map
    const messageContentMap: Record<string, string> = {};
    messageData?.forEach(message => {
      messageContentMap[message.id] = message.content;
    });
    
    // Extract topics from message content
    const positiveTopics: Record<string, number> = {};
    const negativeTopics: Record<string, number> = {};
    
    filteredFeedback.forEach(entry => {
      const content = messageContentMap[entry.message_id] || '';
      if (!content) return;
      
      // Extract keywords with improved multilingual support
      const keywords = extractKeywords(content);
      const targetDict = entry.feedback_type === 'positive' ? positiveTopics : negativeTopics;
      
      keywords.forEach(keyword => {
        targetDict[keyword] = (targetDict[keyword] || 0) + 1;
      });
    });
    
    const topPositiveTopics = Object.entries(positiveTopics)
      .map(([topic, count]) => ({ topic, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);
    
    const topNegativeTopics = Object.entries(negativeTopics)
      .map(([topic, count]) => ({ topic, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);
    
    return {
      feedbackTrend,
      topPositiveTopics,
      topNegativeTopics,
      sessionTopics,
      // Add empty arrays for these properties to avoid undefined errors
      knowledgeImprovementSuggestions: [],
      ruleImprovementSuggestions: []
    };
  } catch (error) {
    console.error('Error analyzing feedback:', error);
    throw error;
  }
}

// Function to track feedback when user provides it
export async function trackFeedback(
  messageId: string,
  sessionId: string,
  userId: string,
  feedbackType: 'positive' | 'negative',
  contentSample?: string
): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('assistant_feedback')
      .upsert({
        message_id: messageId,
        session_id: sessionId,
        user_id: userId,
        feedback_type: feedbackType,
        content_sample: contentSample,
      }, {
        onConflict: 'message_id,user_id'
      });
    
    if (error) {
      console.error('Error tracking feedback:', error);
      return false;
    }
    
    return true;
  } catch (error) {
    console.error('Error in trackFeedback:', error);
    return false;
  }
}

export async function getKnowledgeEffectiveness(
  startDate: Date,
  endDate: Date,
  topic?: string
): Promise<any[]> {
  try {
    let query = supabase
      .from('knowledge_effectiveness')
      .select(`
        id,
        knowledge_document_id,
        relevance_score,
        was_used,
        feedback_score,
        created_at,
        assistant_knowledge:knowledge_document_id (title)
      `)
      .gte('created_at', startDate.toISOString())
      .lte('created_at', endDate.toISOString())
      .order('relevance_score', { ascending: false });
      
    if (topic) {
      // We'd need to join with sessions to filter by topic
      // This is just a placeholder for now
    }
    
    const { data, error } = await query;
    
    if (error) {
      console.error('Error fetching knowledge effectiveness:', error);
      return [];
    }
    
    return data || [];
  } catch (error) {
    console.error('Error in getKnowledgeEffectiveness:', error);
    return [];
  }
}
